import {FieldError} from 'react-hook-form';
import {Breakpoint} from 'antd/es/_util/responsiveObserve';
import {OptionType} from 'components/form/Select';
import {appState} from 'store/AppStore';
import {CarListDto, CustomerDto, OrderedFileDto, ReservationDto} from '../../../../PRMGApiClient';
import {dateFormat, dateHoursFormat, timeFormat} from './formats';
import {Typography} from 'antd';
import moment from 'moment';

export function getErrorMessage(error: FieldError | Array<FieldError | undefined>, fieldLabel?: string): string {
  if (!Array.isArray(error)) {
    if (error.message && error.message !== '') {
      return error.message;
    }

    if (error.type === 'required') {
      return `${fieldLabel ? fieldLabel : 'This field'} is required.`;
    }

    if (error.type === 'minLength') {
      return `${fieldLabel ? fieldLabel : 'This field'} is not long enough.`;
    }

    if (error.type === 'maxLength') {
      return `${fieldLabel ? fieldLabel : 'This field'} is too long.`;
    }
  } else {
    return error.map((singleError) => singleError?.message).join(', ');
  }

  return 'Error';
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function isFile(file: File | OrderedFileDto): file is File {
  return (file as File).name !== undefined;
}

export function getIsMobile({md, lg, xl, xxl}: Partial<Record<Breakpoint, boolean>>) {
  return !md && !lg && !xl && !xxl;
}
export function getIsTablet({md, lg}: Partial<Record<Breakpoint, boolean>>) {
  return md && !lg;
}

export function mapCarsToOptions(cars: Array<CarListDto | undefined>): Array<OptionType> {
  return cars
    .map((car): OptionType => {
      if (car && car.carId && car.carMakeName && car.carModelName) {
        return {value: car.carId, label: `${car.carMakeName} ${car.carModelName} ${car.plateNumber}`};
      }
      return {value: null, label: 0};
    })
    .filter((option) => option.value !== null);
}

export function mapCustomersToOptions(customers: Array<CustomerDto | undefined>): Array<OptionType> {
  return customers
    .map((customer): OptionType => {
      if ((customer && customer.customerId && customer.companyName) || (customer?.firstName && customer?.lastName)) {
        return {
          value: Number(customer.customerId),
          label: customer.companyName ?? `${customer.firstName} ${customer.lastName}`,
        };
      }
      return {value: null, label: 0};
    })
    .filter((option) => option.value !== null);
}

export const getFirstDayOfPrevMonth = (date: Date): Date => {
  return moment(date).subtract(1, 'months').startOf('month').toDate();
};

export const getLastDayOfNextMonth = (date: Date): Date => {
  return moment(date).add(1, 'months').endOf('month').toDate();
};

export function getFirstDayOfYear(date: Date): Date {
  return moment(date).startOf('year').toDate();
}

export function getLastDayOfYear(date: Date): Date {
  return moment(date).endOf('year').toDate();
}

export function areDatesDayEqual(startDate?: Date, endDate?: Date) {
  return startDate && endDate && moment(startDate).isSame(endDate, 'day');
}

export function showDatesRange(startDate?: Date, endDate?: Date) {
  const isAllDay = checkIsAllDay(startDate, endDate);

  if (areDatesDayEqual(startDate, endDate)) {
    return isAllDay
      ? dateFormat(startDate)
      : `${dateFormat(startDate)} | ${timeFormat(startDate)} - ${timeFormat(endDate)}`;
  } else {
    return `${dateHoursFormat(startDate)} - ${dateHoursFormat(endDate)}`;
  }
}

export function renderDate(isAllDay: boolean, date?: Date) {
  return isAllDay ? (
    <Typography.Text>{dateFormat(date)}</Typography.Text>
  ) : (
    <Typography.Text>{dateHoursFormat(date)}</Typography.Text>
  );
}

export function checkIsAllDay(start?: Date, end?: Date): boolean {
  if (!start || !end) {
    return false;
  }
  return end.getHours() - start.getHours() === 23 && end.getMinutes() - start.getMinutes() === 59;
}

export const imageErrorCallback = async (
  event: React.SyntheticEvent<HTMLImageElement, Event>,
  file: OrderedFileDto
) => {
  const target = event.currentTarget;
  target.onerror = null;
  target.style.display = 'none';

  if (!file?.fileId) return;

  const link = await appState.refreshFileLink(file?.fileId);
  if (link) {
    target.style.display = 'block';
    target.src = link;
    file.url = link;
  }
};

export function datesBetween(start?: Date, end?: Date): Date[] {
  if (!start || !end) {
    return [];
  }
  const from = moment(start);
  const to = moment(end);
  const nbDays = to.diff(from, 'days') + 1;
  const result = [...Array(nbDays).keys()].map((i) => from.clone().add(i, 'd'));
  return result.map((x) => x.toDate());
}

export function getReservationsActiveDates(reservations?: Array<ReservationDto> | null): Date[] {
  if (!reservations) {
    return [];
  }

  return reservations.map((x) => datesBetween(x.begins, x.ends)).flat();
}

export function loadExampleOrDefaults<T>(demoValues: T, defaults?: T): T | undefined {
  return window.location.hash.includes('example') ? demoValues : defaults;
}
